/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Button from '@Button';
import Text from '@Text';
import { setSelectedLocation } from '../../Lib/redux/slices/clientApiJourney';
import {
  buildGlowMarkerSvgUrl,
  loadGoogleMapsScript,
} from './googleMaps.helpers';
import {
  getById,
  getByTestId,
  outerHeight,
  verticalMargins,
  useOlbScrollMaxHeight,
  journeyProgressHeight,
} from './clientApiLayout.helpers';
import makeStoreMapStyles from './makeStoreMapStyles';
import useClientApiCopy from './copy/useClientApiCopy';
import { resolveLanguage } from './copy/clientApiCopy';

const SINGLE_STORE_ZOOM = 14;

const hasCoordinates = (location) =>
  typeof location.latitude === 'number' &&
  typeof location.longitude === 'number';

const toPosition = (location) => ({
  lat: location.latitude,
  lng: location.longitude,
});

/**
 * Map view of the store list: a map with a numbered marker per store above the
 * same store list. Selecting a marker (or a card) highlights that store — the
 * selection is shared with the list view via the clientApiJourney store — then
 * the user confirms with Continue. Laid out as a flex column that fills the
 * available height: the map header and Continue stay fixed while the store
 * cards scroll (invisible scrollbar) in between. The Google Maps script is
 * loaded lazily once the map opens.
 */
const StoreMap = ({ locations, onStoreSelect }) => {
  const theme = useTheme();
  const classes = makeStoreMapStyles({ theme });
  const copy = useClientApiCopy();
  const localeCode = useSelector((store) => store.formConfig?.localeCode);
  const dispatch = useDispatch();
  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);
  const storeMapRef = useRef(null);
  const [isUnavailable, setIsUnavailable] = useState(false);

  const selectedLocation = useSelector(
    (store) => store.clientApiJourney.location?.selected
  );
  const isDynamicHeightEnabled = useSelector(
    (store) => store.optionalFormConfig?.isDynamicHeightEnabled
  );
  const selectedId = selectedLocation?.id ?? null;
  const glowColor = theme.mvf.palette.component.answerSelectedColor;

  // Bound the whole map view (map + legend + cards + Continue) to the form's
  // remaining space so the cards scroll while the map and Continue stay visible.
  useOlbScrollMaxHeight(
    storeMapRef,
    () => {
      const formContent = getById('formContentContainer');
      if (!formContent) return 0;
      return (
        formContent.clientHeight -
        (formContent.children?.[0]?.clientHeight ?? 0) -
        journeyProgressHeight() -
        outerHeight(getByTestId('storeListTitle')) -
        outerHeight(getByTestId('storeListPostcode')) -
        outerHeight(getByTestId('storeViewToggle')) -
        verticalMargins(getByTestId('storeMapHeader')) -
        (getById('footerContainer')?.clientHeight ?? 0)
      );
    },
    [locations, selectedId, isDynamicHeightEnabled],
    !isDynamicHeightEnabled
  );

  const highlightStore = (location) =>
    dispatch(setSelectedLocation({ selectedLocation: location }));

  // Give the chosen store's pin a themed glow icon + bold number; the rest keep
  // the default Google pin. Safe to call before the markers exist (no-ops).
  const paintMarkers = (activeId) => {
    const { google } = window;
    if (!google) return;
    markersRef.current.forEach(({ id, label, marker }) => {
      if (id === activeId) {
        marker.setIcon({
          url: buildGlowMarkerSvgUrl(label, glowColor),
          scaledSize: new google.maps.Size(40, 52),
          anchor: new google.maps.Point(20, 46),
        });
        marker.setLabel(null);
        marker.setZIndex(1000);
      } else {
        marker.setIcon(null);
        marker.setLabel(String(label));
        marker.setZIndex(undefined);
      }
    });
  };

  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    const pinnableLocations = (locations ?? []).filter(hasCoordinates);

    if (!apiKey || pinnableLocations.length === 0) {
      setIsUnavailable(true);
      return undefined;
    }

    let isActive = true;

    loadGoogleMapsScript(apiKey, resolveLanguage(localeCode))
      .then(() => {
        if (!isActive || !mapContainerRef.current) return;

        const { google } = window;
        const map = new google.maps.Map(mapContainerRef.current, {
          center: toPosition(pinnableLocations[0]),
          zoom: SINGLE_STORE_ZOOM,
          disableDefaultUI: true,
          zoomControl: true,
        });
        const bounds = new google.maps.LatLngBounds();

        // Number the pins by their position in the full list so they line up
        // with the numbered cards below.
        markersRef.current = (locations ?? [])
          .map((location, index) => {
            if (!hasCoordinates(location)) return null;
            const position = toPosition(location);
            const marker = new google.maps.Marker({
              position,
              map,
              label: String(index + 1),
              title: location.title,
            });
            marker.addListener('click', () => highlightStore(location));
            bounds.extend(position);
            return { id: location.id, label: index + 1, marker };
          })
          .filter(Boolean);

        if (pinnableLocations.length > 1) {
          map.fitBounds(bounds);
        }

        // Apply the initial selection glow once the markers exist.
        paintMarkers(selectedId);
      })
      .catch(() => {
        if (isActive) setIsUnavailable(true);
      });

    return () => {
      isActive = false;
      markersRef.current.forEach(({ marker }) => marker.setMap(null));
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Repaint the highlighted pin whenever the shared selection changes.
  useEffect(() => {
    paintMarkers(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const handleContinue = () => {
    if (selectedLocation) onStoreSelect(selectedLocation);
  };

  if (isUnavailable) {
    return (
      <Box css={classes.messageContainer} data-testid="storeMapUnavailable">
        <Text variant="text">{copy.store.mapUnavailable}</Text>
      </Box>
    );
  }

  return (
    <Box css={classes.root} ref={storeMapRef} data-testid="storeMap">
      <Box css={classes.mapHeader} data-testid="storeMapHeader">
        <Box ref={mapContainerRef} css={classes.map} />
        <Box css={classes.legend}>
          <Box css={classes.legendMarker} />
          <Text variant="text">{copy.store.storesLegend}</Text>
        </Box>
      </Box>

      <Box css={classes.cardScrollArea}>
        {(locations ?? []).map((location, index) => {
          const isSelected = location.id === selectedId;
          return (
            <MuiButton
              key={location.id}
              css={[classes.storeCard, isSelected && classes.storeCardSelected]}
              onClick={() => highlightStore(location)}
              aria-pressed={isSelected}
              disableRipple
              data-testid="mapStoreCard"
            >
              <Box css={classes.storeCardRow}>
                <Box css={classes.storeNumber}>
                  <Text variant="text">{index + 1}</Text>
                </Box>
                <Box css={classes.storeCardBody}>
                  <Box css={classes.storeCardHeader}>
                    <Box css={classes.storeName}>
                      <Text variant="text">{location.title}</Text>
                    </Box>
                    <Box css={classes.storeDistance}>
                      <Text variant="text">
                        {location.distance} {location.distanceUnit}
                      </Text>
                    </Box>
                  </Box>
                  <Box css={classes.storeAddress}>
                    <Text variant="text">
                      <svg
                        css={classes.addressPin}
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      {location.address}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </MuiButton>
          );
        })}
      </Box>

      <Box css={classes.continueContainer}>
        <Button
          label={copy.store.continue}
          onClick={handleContinue}
          disabled={!selectedLocation}
          dataTestId="storeMapContinue"
        />
      </Box>
    </Box>
  );
};

export default StoreMap;
